import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Descarga una plantilla Excel CON CORREO para CREAR usuarios nuevos
 */
export const descargarPlantillaCrearUsuarios = () => {
  const datosEjemplo = [
    {
      NOMBRE: '',
      APELLIDO: '',
      DNI: '',
      CORREO: ''
    }
  ];

  const ws = XLSX.utils.json_to_sheet(datosEjemplo);

  ws['!cols'] = [
    { wch: 20 }, // NOMBRE
    { wch: 20 }, // APELLIDO
    { wch: 12 }, // DNI
    { wch: 30 }  // CORREO
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  saveAs(data, `Plantilla_Crear_Usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Descarga una plantilla Excel SIN CORREO para ACTUALIZAR usuarios existentes
 */
export const descargarPlantillaActualizarUsuarios = () => {
  const datosEjemplo = [
    {
      NOMBRE: '',
      APELLIDO: '',
      DNI: ''
    }
  ];

  const ws = XLSX.utils.json_to_sheet(datosEjemplo);

  ws['!cols'] = [
    { wch: 20 }, // NOMBRE
    { wch: 20 }, // APELLIDO
    { wch: 12 }  // DNI
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  saveAs(data, `Plantilla_Actualizar_Usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * @deprecated Usar descargarPlantillaCrearUsuarios o descargarPlantillaActualizarUsuarios
 */
export const descargarPlantillaUsuarios = descargarPlantillaActualizarUsuarios;

/**
 * Lee un archivo Excel CON CORREO y retorna los datos de usuarios (para CREAR)
 * @param {File} file - Archivo Excel
 * @returns {Promise<Array>} - Array de objetos con datos de usuarios
 */
export const leerArchivoExcelConCorreo = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        const usuariosValidados = jsonData.map((row, index) => {
          const nombre = row.NOMBRE?.toString().trim();
          const apellido = row.APELLIDO?.toString().trim();
          let dni = row.DNI?.toString().trim();
          const correo = row.CORREO?.toString().trim();

          if (!nombre || !apellido || !dni) {
            throw new Error(`Fila ${index + 2}: Faltan datos obligatorios (NOMBRE, APELLIDO o DNI)`);
          }

          if (dni.length > 8) {
            dni = dni.substring(0, 8);
          }

          if (dni.length < 1) {
            throw new Error(`Fila ${index + 2}: DNI vacío`);
          }

          return {
            nombre: nombre.toUpperCase(),
            apellido: apellido.toUpperCase(),
            dni: dni,
            correo: correo || `${dni}@postulante.com` // Si no hay correo, genera uno
          };
        });

        if (usuariosValidados.length === 0) {
          throw new Error('El archivo no contiene datos válidos');
        }

        resolve(usuariosValidados);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(new Error('Error al leer el archivo: ' + error));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Lee un archivo Excel SIN CORREO y retorna los datos de usuarios (para ACTUALIZAR)
 * @param {File} file - Archivo Excel
 * @returns {Promise<Array>} - Array de objetos con datos de usuarios
 */
export const leerArchivoExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Obtener la primera hoja
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convertir a JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validar y transformar datos
        const usuariosValidados = jsonData.map((row, index) => {
          const nombre = row.NOMBRE?.toString().trim();
          const apellido = row.APELLIDO?.toString().trim();
          let dni = row.DNI?.toString().trim();

          // Validaciones básicas
          if (!nombre || !apellido || !dni) {
            throw new Error(`Fila ${index + 2}: Faltan datos obligatorios (NOMBRE, APELLIDO o DNI)`);
          }

          // Truncar DNI a 8 dígitos si tiene más (acepta de 6 a 8 dígitos)
          if (dni.length > 8) {
            dni = dni.substring(0, 8);
          }

          // Validar que tenga al menos un dígito
          if (dni.length < 1) {
            throw new Error(`Fila ${index + 2}: DNI vacío`);
          }

          return {
            nombre: nombre.toUpperCase(),
            apellido: apellido.toUpperCase(),
            dni: dni
          };
        });

        if (usuariosValidados.length === 0) {
          throw new Error('El archivo no contiene datos válidos');
        }

        resolve(usuariosValidados);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(new Error('Error al leer el archivo: ' + error));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Valida que el archivo sea de tipo Excel
 * @param {File} file 
 * @returns {boolean}
 */
export const validarArchivoExcel = (file) => {
  const extensionesValidas = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel' // .xls
  ];
  
  return extensionesValidas.includes(file.type) || 
         file.name.endsWith('.xlsx') || 
         file.name.endsWith('.xls');
};
